import { TTemporalDataResponseSerie } from "@/components/ui/chart/types/temporal-chart-series.type";
import { chain } from "@/utils/chain";
import { ICalendarHeatmapAdapter } from "@/utils/chart/adapters/types/calendar-heatmap-adapter.type";
import { ICommonSeriesAdapterOptions } from "@/utils/chart/adapters/types/common-adapter.type";
import $date from "@/utils/date";
import { EPrefixSymbol, getValueByPrefix } from "@/utils/translation-utils";

interface ICalendarHeatmapSeriesAdapterOptions extends ICommonSeriesAdapterOptions {
  accessibilityDescription: string;
  tooltipNullText: string;
  prefixSymbol: EPrefixSymbol;
}

export function CalendarHeatmapSeriesAdapter<KIND extends string>(
  opts: Partial<ICalendarHeatmapSeriesAdapterOptions> = {},
): ICalendarHeatmapAdapter<KIND> {
  return {
    adapt(serie: TTemporalDataResponseSerie<KIND>): Array<Highcharts.SeriesOptionsType> {
      const unit = serie.unit;

      const points = serie.value.map((point) => {
        return {
          ...point,
          value: typeof point.value === "number" ? getValueByPrefix(point.value, opts.prefixSymbol) : null,
        };
      });

      const sortedPoints = chain(points)
        .sortBy((_) => new Date(_.date).getTime())
        .value();

      const data: Highcharts.SeriesHeatmapOptions["data"] = (() => {
        const maybeFirstPoint = sortedPoints[0];
        if (!maybeFirstPoint) return [];

        const firstWeekDay = $date.getDateWeekDay(maybeFirstPoint.date);

        return sortedPoints.map((point, i) => {
          const weekday = $date.getDateWeekDay(point.date);

          return {
            x: Math.floor((i + firstWeekDay) / 7),
            y: weekday,
            value: point.value,
            custom: {
              date: new Date(point.date).getTime(),
            },
          };
        });
      })();

      const results: Array<Highcharts.SeriesOptionsType> = [];

      results.push({
        id: serie.category,
        name: opts.name ?? serie.category,
        type: "heatmap",
        data: data,
        accessibility: {
          description: opts.accessibilityDescription,
        },
        tooltip: {
          headerFormat: "",
          pointFormat: `{point.custom.date:%e %b %Y} <br/> <b>{point.value} ${opts.prefixSymbol ?? ""}${unit}</b>`,
          nullFormat: `{point.custom.date:%e %b %Y} <br/> ${opts.tooltipNullText ?? ""}`,
        },
      });

      return results;
    },
  };
}
